import React, { useState, useEffect, useCallback} from "react";
import HeaderIcon from "../../Images/headericon.png";
import { useFormik } from "formik";
import {
  createTheme,
  ThemeProvider,
  CssBaseline,
  Paper,
  Button,
  Typography,
  FormControlLabel,
  Box,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  MenuItem,
  Dialog,
  Checkbox,
  Select,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
} from "@mui/material";
import { sanitizeQuestionType, questionTypes } from "../../Dummy/columns";
import {
  CreateKYC,
  DeleteKYC,
  GetUserKyc,
  GetIBKRkycmapping,
  GetCustodianTable,
  RestoreQuestion,
  DeleteSection
} from "../../redux/Actions/userStatusService";
import { useDispatch } from "react-redux";
import { kycSchema } from "../Schemas/KycManagement";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import AddTabModal from "../../Modals/AddTabModal";
import { ExpandMore } from "@mui/icons-material";
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import Menu from '@mui/material/Menu';
import './KycManagement.css';

// Helper functions
const isTextOrNumberType = (type) => {
  if (!type) return false;
  const upperType = type.toString().toUpperCase();
  return ['TEXTBOX', 'NUMERIC', 'PHONE'].includes(upperType);
};

const isOptionBasedType = (type) => {
  if (!type) return false;
  const upperType = type.toString().toUpperCase();
  return ['DROPDOWN', 'RADIO', 'CHECKBOX', 'BOOLEAN'].includes(upperType);
};

const getConditionalQuestions = (organizedQuestions) => {
  const allQuestions = Object.values(organizedQuestions)
    .flatMap(sectionData => Object.values(sectionData).flat());
  return allQuestions.filter(question => 
    isOptionBasedType(question.type || question.inputType)
  );
};

const mergeIBKRData = (userKycData, ibkrMappingData) => {  
  const existingQuestionsByField = userKycData.reduce((acc, question) => {
    if (question.field) {
      acc[question.field] = question;
    }
    return acc;
  }, {});
  
  const processedFields = new Set();
  const mergedData = [];
  
  userKycData.forEach(question => {
    if (question.field) {
      processedFields.add(question.field);
    }
    mergedData.push(question);
  });
  
  ibkrMappingData.forEach((ibkrItem, index) => {
    if (processedFields.has(ibkrItem.field)) {
      return;
    }
    
    mergedData.push({
      _id: `ibkr_${index}`,
      id: `ibkr_${index}`,
      text: ibkrItem.description || '',
      type: '',
      inputType: '',
      required: false,
      section: 'Default Section',
      field: ibkrItem.field,
      custodians: ['IBKR'],
      order: userKycData.length + index,
      status: 'ACTIVE',
      limit: '',
      format: '',
      options: [],
      answers: [],
      condition: []
    });
    
    processedFields.add(ibkrItem.field);
  });
  return mergedData;
};

const Index = () => {
  const dispatch = useDispatch();
  const theme = createTheme();

  const [selectedUserType, setSelectedUserType] = useState(null);
  const [modalOpen, setModalOpen] = useState(false); 
  const [tabValue, setTabValue] = useState(0);
  const [apiQuestions, setApiQuestions] = useState([]);
  const [organizedQuestions, setOrganizedQuestions] = useState({});
  const [tabNames, setTabNames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [custodianData, setCustodianData] = useState([]);
  const [selectedCustodians, setSelectedCustodians] = useState([]);
  const [sections, setSections] = useState([]);
  const [isAddingSection, setIsAddingSection] = useState(false);
  const [newSectionName, setNewSectionName] = useState('');
  const [showRoleSection, setShowRoleSection] = useState(false);
  const [subsections, setSubsections] = useState({}); // { sectionName: [subsection1, subsection2, ...] }
  const [isAddingSubsection, setIsAddingSubsection] = useState(false);
  const [newSubsectionName, setNewSubsectionName] = useState('');
  const [activeSubsection, setActiveSubsection] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    open: false,
    questionId: null,
    isNew: false
  });
  const [confirmationDialog, setConfirmationDialog] = useState({
    open: false,
    action: null,
    questionId: null,
  });
  const [questionsReordered, setQuestionsReordered] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [activeQuestionId, setActiveQuestionId] = useState(null);
  const [deleteSectionConfirmation, setDeleteSectionConfirmation] = useState({
    open: false,
    sectionName: null
  });

  // Helper functions
  const organizeQuestions = (questions) => {
    return questions.reduce((acc, question) => {
      const section = question.section || 'Default';
      const subSection = question.subSection || 'Default';
      
      if (!acc[section]) {
        acc[section] = {};
      }
      if (!acc[section][subSection]) {
        acc[section][subSection] = [];
      }
      
      const transformedQuestion = {
        _id: question._id,
        id: question._id,
        text: question.text,
        type: question.inputType,
        required: question.required,
        options: question.answers || [],
        condition: question.condition || [],
        custodians: question.custodians || [],
        limit: parseInt(question.limit) || '',
        format: question.format || '',
        section: question.section,
        subSection: question.subSection,
        status: question?.status,
        deletedAt: question.deletedAt || null,
        field: question.field || ''
      };
      acc[section][subSection].push(transformedQuestion);
      return acc;
    }, {});
  };

  // Formik setup
  const formik = useFormik({
    initialValues: {
      questions: apiQuestions.map(question => ({
        ...question,
        modified: false
      })),
    },
    validationSchema: kycSchema,
    onSubmit: async (values) => {
      // Submit logic here
    },
  });

  // Improved refresh function with better error handling and state management
  const refreshQuestions = useCallback(async () => {
    if (!selectedUserType || selectedCustodians.length === 0) {
      return;
    }
    
    try {
      setLoading(true);
      
      const userValue = selectedUserType.toUpperCase();
      const getUserKycBody = [{
        type: userValue,
        custodians: selectedCustodians
      }];
      
      const response = await dispatch(GetUserKyc({ body: getUserKycBody }));
      
      if (response?.payload?.statusCode === 200) {
        let kycData = [...(response.payload.data || [])];
        
        // Sort by order
        kycData.sort((a, b) => {
          const orderA = typeof a.order === 'number' ? a.order : 0;
          const orderB = typeof b.order === 'number' ? b.order : 0;
          return orderA - orderB;
        });
        
        // Merge IBKR data if needed
        if (selectedCustodians.includes("IBKR")) {
          try {
            const ibkrResponse = await dispatch(GetIBKRkycmapping(userValue));
            if (ibkrResponse.payload?.statusCode === 200) {
              const ibkrData = ibkrResponse.payload.data || [];
              kycData = mergeIBKRData(kycData, ibkrData);
            }
          } catch (ibkrError) {
            console.error("Error fetching IBKR KYC mapping:", ibkrError);
          }
        }
        
        // Update all states atomically to prevent inconsistencies
        setApiQuestions(kycData);
        
        const organized = organizeQuestions(kycData);
        setOrganizedQuestions(organized);
        
        const uniqueSections = Object.keys(organized);
        setSections(uniqueSections);
        
        // Initialize subsections for each section
        const subsectionsData = {};
        Object.keys(organized).forEach(section => {
          subsectionsData[section] = Object.keys(organized[section]);
        });
        setSubsections(subsectionsData);
        
        // Update formik with current section data
        const currentSection = uniqueSections[tabValue] || uniqueSections[0];
        if (currentSection) {
          // Get the first subsection's questions for formik
          const firstSubsection = Object.keys(organized[currentSection] || {})[0] || 'Default';
          formik.setFieldValue("questions", organized[currentSection]?.[firstSubsection] || []);
        }
        
        // Reset reorder flag
        setQuestionsReordered(false);
        
      } else {
        console.error("Failed to refresh questions:", response?.payload?.message);
      }
    } catch (error) {
      console.error("Error refreshing questions:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedUserType, selectedCustodians, tabValue, dispatch]);

  // Fetch custodians
  useEffect(() => {
    const fetchCustodians = async () => {
      try {
        const response = await dispatch(GetCustodianTable());
        const custodians = response?.payload?.data || [];
        setCustodianData(custodians);
      } catch (error) {
        console.error("Error fetching custodians:", error);
      }
    };
    fetchCustodians();
  }, [dispatch]);

  // Auto-refresh questions when userType or custodians change
  useEffect(() => {
    if (selectedUserType && selectedCustodians.length > 0) {
      refreshQuestions();
    }
  }, [selectedUserType, selectedCustodians, refreshQuestions]);

  const handleCustodianClick = async (custodianName) => {
    // Clear all existing state
    setApiQuestions([]);
    setOrganizedQuestions({});
    setSections([]);
    setSelectedQuestion(null);
    setActiveSection(null);
    formik.resetForm({ values: { questions: [] } });
    setLoading(true);

    setSelectedCustodians([custodianName]);
    setShowRoleSection(true);
    
    setSelectedUserType(null);
    setTabValue(0);
    setTabNames([]);
    setLoading(false);
  };

  const handleUserTypeChange = async (event) => {
    const userType = event.target.value;
    setSelectedUserType(userType);
    setTabValue(0);
    
    // Clear existing state
    setSelectedQuestion(null);
    setActiveSection(null);
    formik.resetForm({ values: { questions: [] } });

    // Data will be automatically fetched by useEffect when selectedUserType changes
  };

  const handleAddSection = () => {
    setIsAddingSection(true);
  };

  const handleSectionNameSubmit = (e) => {
    if (e.key === 'Enter' && newSectionName.trim()) {
      const sectionName = newSectionName.trim();
      setSections([...sections, sectionName]);
      
      // Initialize the new section with a Default subsection
      setOrganizedQuestions(prev => ({
        ...prev,
        [sectionName]: {
          'Default': []
        }
      }));
      
      // Update subsections state
      setSubsections(prev => ({
        ...prev,
        [sectionName]: ['Default']
      }));
      
      setNewSectionName('');
      setIsAddingSection(false);
    }
  };

  // Subsection management functions
  const handleAddSubsection = (sectionName) => {
    setActiveSubsection(sectionName);
    setIsAddingSubsection(true);
  };

  const handleSubsectionNameSubmit = (e) => {
    if (e.key === 'Enter' && newSubsectionName.trim() && activeSubsection) {
      const newSubsection = newSubsectionName.trim();
      setSubsections(prev => ({
        ...prev,
        [activeSubsection]: [...(prev[activeSubsection] || []), newSubsection]
      }));
      
      // Initialize empty subsection in organizedQuestions
      setOrganizedQuestions(prev => ({
        ...prev,
        [activeSubsection]: {
          ...prev[activeSubsection],
          [newSubsection]: []
        }
      }));
      
      setNewSubsectionName('');
      setIsAddingSubsection(false);
      setActiveSubsection(null);
    }
  };

  const handleDeleteSubsection = (sectionName, subsectionName) => {
    setSubsections(prev => ({
      ...prev,
      [sectionName]: prev[sectionName].filter(sub => sub !== subsectionName)
    }));
    
    // Remove subsection from organizedQuestions
    setOrganizedQuestions(prev => {
      const newOrganized = { ...prev };
      if (newOrganized[sectionName] && newOrganized[sectionName][subsectionName]) {
        delete newOrganized[sectionName][subsectionName];
      }
      return newOrganized;
    });
  };

  const handleSubsectionVisibilityToggle = async (sectionName, subsectionName, event) => {
    event.stopPropagation();
    
    try {
      setLoading(true);
      
      const subsectionQuestions = organizedQuestions[sectionName]?.[subsectionName] || [];
      if (subsectionQuestions.length === 0) return;
      
      const isCurrentlyDeleted = isSubsectionSoftDeleted(sectionName, subsectionName, organizedQuestions);
      const currentTime = new Date().toISOString();
      
      // Toggle delete status for all questions in the subsection
      const updatedQuestions = subsectionQuestions.map(question => ({
        ...question,
        deletedAt: isCurrentlyDeleted ? null : currentTime,
        modified: true
      }));
      
      // Update the organizedQuestions state
      setOrganizedQuestions(prev => ({
        ...prev,
        [sectionName]: {
          ...prev[sectionName],
          [subsectionName]: updatedQuestions
        }
      }));
      
      // Update questions in the API
      const questionsToUpdate = updatedQuestions.map(question => cleanQuestionObject(question));
      
      const response = await dispatch(CreateKYC({ 
        body: questionsToUpdate, 
        method: "PATCH" 
      }));
      
      if (response?.payload?.statusCode === 200) {
        // Wait a moment for the API to process
        await new Promise(resolve => setTimeout(resolve, 500));
        await refreshQuestions();
      } else {
        console.error("Failed to update subsection visibility:", response?.payload?.message);
      }
    } catch (error) {
      console.error("Error toggling subsection visibility:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuestionSelect = (question, section) => {
    // Check if question has existing conditions to determine if conditional toggle should be on
    const hasExistingConditions = question.condition && Array.isArray(question.condition) && question.condition.length > 0;
    
    if (selectedCustodians.includes("IBKR")) {
      const baseQuestion = {
        ...question,
        field: question.field,
        textInput: question.text || '', // Initialize input field with existing text
        conditionalEnabled: hasExistingConditions // Set toggle based on existing conditions
      };

      if (question.type || question.inputType) {
        setSelectedQuestion({
          ...baseQuestion,
          isNew: false,
          condition: question.condition || []
        });
      } else {
        setSelectedQuestion({
          ...baseQuestion,
          isNew: true,
          type: '',
          inputType: '',
          required: false,
          options: [],
          condition: [],
          limit: '',
          format: '',
          textInput: question.text || '',
          conditionalEnabled: false // New questions start with conditional disabled
        });
      }
    } else {
      setSelectedQuestion({
        ...question,
        condition: question.condition || [],
        textInput: question.text || '', // Initialize input field with existing text
        conditionalEnabled: hasExistingConditions // Set toggle based on existing conditions
      });
    }
    setActiveSection(section);
  };

  const handleAddNewQuestion = (section, subsection = 'Default') => {
    const sectionQuestions = organizedQuestions[section]?.[subsection] || [];
    let highestOrder = 0;
    
    sectionQuestions.forEach(question => {
      if (typeof question.order === 'number' && question.order > highestOrder) {
        highestOrder = question.order;
      }
    });
    
    const nextOrder = highestOrder + 1;
    
    setSelectedQuestion({
      isNew: true,
      section: section,
      subSection: subsection,
      text: '', // Preview field
      textInput: '', // Input field where user types
      required: false,
      type: 'TEXTBOX',
      options: [],
      condition: [],
      conditionalEnabled: false, // New questions start with conditional disabled
      limit: '',
      format: '',
      order: nextOrder
    });
    setActiveSection(section);
  };

  const handleQuestionChange = (questionId, field, value) => {
    setSelectedQuestion(prev => {
      const updates = {
        ...prev,
        [field]: value,
        field: prev.field
      };

      // Sync text changes: textInput (where user types) updates text (preview)
      if (field === 'textInput') {
        updates.text = value; // Update the preview field
        updates.textInput = value; // Update the input field
      }

      if (field === 'limit' && value) {
        updates.format = '';
        updates.showFormatField = false;
      }
      
      if (field === 'format' && value) {
        updates.limit = '';
        updates.showLimitField = false;
      }

      if (field === 'type' && isOptionBasedType(value) && !prev.options) {
        updates.options = [];
      }

      if (field === 'type' && !prev.condition) {
        updates.condition = [];
      }

      // Handle conditional toggle - clear conditions when disabled
      if (field === 'conditionalEnabled') {
        if (!value) {
          updates.condition = []; // Clear all conditions when toggle is turned off
        }
      }

      return updates;
    });
  };

  const handleConditionalChange = (questionId, index, field, value) => {
    setSelectedQuestion(prev => {
      const updatedConditions = [...(prev.condition || [])];
      updatedConditions[index] = {
        ...updatedConditions[index],
        [field]: value
      };
      return {
        ...prev,
        condition: updatedConditions
      };
    });
  };

  const addConditionalQuestion = (questionId) => {
    setSelectedQuestion(prev => ({
      ...prev,
      condition: [...(prev.condition || []), { questionOrder: '', value: '' }]
    }));
  };

  const removeConditionalQuestion = (questionId, index) => {
    setSelectedQuestion(prev => ({
      ...prev,
      condition: prev.condition.filter((_, i) => i !== index)
    }));
  };

  const isConditionComplete = (condition) => {
    return condition.questionOrder && condition.value;
  };

  const getConditionRowStyle = (condition) => {
    if (isConditionComplete(condition)) {
      return {
        borderColor: '#4caf50',
        backgroundColor: '#f1f8e9'
      };
    } else if (condition.questionOrder && !condition.value) {
      return {
        borderColor: '#ff9800',
        backgroundColor: '#fff3e0'
      };
    }
    return {};
  };

  const isFormValid = () => {
    if (!selectedQuestion) return false;
    
    if (!selectedQuestion.text || selectedQuestion.text.trim() === '') {
      return false;
    }
    
    if (!selectedQuestion.type || selectedQuestion.type.trim() === '') {
      return false;
    }
    
    // Only validate conditions if they exist and conditional questions are enabled
    if (selectedQuestion.conditionalEnabled && selectedQuestion.condition && selectedQuestion.condition.length > 0) {
      const incompleteConditions = selectedQuestion.condition.filter(cond => !isConditionComplete(cond));
      if (incompleteConditions.length > 0) {
        return false;
      }
    }
    
    if (isOptionBasedType(selectedQuestion.type)) {
      if (!selectedQuestion.options || selectedQuestion.options.length === 0) {
        return false;
      }
      const emptyOptions = selectedQuestion.options.filter(option => !option || option.trim() === '');
      if (emptyOptions.length > 0) {
        return false;
      }
    }
    
    return true;
  };

  const getValidationMessages = () => {
    const messages = [];
    
    if (!selectedQuestion) return messages;
    
    if (!selectedQuestion.text || selectedQuestion.text.trim() === '') {
      messages.push('Question text');
    }
    
    if (!selectedQuestion.type || selectedQuestion.type.trim() === '') {
      messages.push('Question type');
    }
    
    if (isOptionBasedType(selectedQuestion.type) && (!selectedQuestion.options || selectedQuestion.options.length === 0)) {
      messages.push(`Options for ${selectedQuestion.type.toLowerCase()} question`);
    }
    
    // Only validate conditions if conditional questions are enabled
    if (selectedQuestion.conditionalEnabled && selectedQuestion.condition && selectedQuestion.condition.length > 0) {
      const incompleteConditions = selectedQuestion.condition.filter(cond => !isConditionComplete(cond));
      if (incompleteConditions.length > 0) {
        messages.push('Complete all condition fields');
      }
    }
    
    return messages;
  };

  const cleanQuestionObject = (question) => {
    const cleanedQuestion = {
      _id: question._id || question.id,
      text: question.text,
      type: selectedUserType.toUpperCase(),
      inputType: sanitizeQuestionType(question.type || question.inputType),
      custodians: selectedCustodians.length > 0 ? selectedCustodians : [],
      section: question.section,
      subSection: question.subSection || 'Default',
      order: question.order,
      required: !!question.required,
    };

    if (question.entities && question.entities.length > 0) {
      cleanedQuestion.entities = question.entities;
    }

    if (question.options && question.options.length > 0) {
      cleanedQuestion.answers = question.options;
    } else if (question.answers && question.answers.length > 0) {
      cleanedQuestion.answers = question.options;
    } else {
      cleanedQuestion.answers = [];
    }

    if (question.field && question.field.trim() !== '') {
      cleanedQuestion.field = question.field;
    }
    
    if (question.limit !== undefined && question.limit !== null) {
      const limitStr = question.limit.toString().trim();
      if (limitStr !== '' && limitStr !== 'string') {
        cleanedQuestion.limit = parseInt(question.limit);
      }
    }
    
    if (question.format !== undefined && question.format !== null) {
      const formatStr = question.format.toString().trim();
      if (formatStr !== '' && formatStr !== 'string') {
        cleanedQuestion.format = question.format;
      }
    }
    
    if (question.condition && Array.isArray(question.condition)) {
      const validConditions = question.condition.filter(cond => {
        if (!cond || typeof cond !== 'object') return false;
        
        const hasQuestionOrder = cond.questionOrder && cond.questionOrder.toString().trim() !== '';
        const hasValue = cond.value && cond.value.toString().trim() !== '';
        
        return hasQuestionOrder && hasValue;
      });
      
      cleanedQuestion.condition = validConditions;
    } else {
      cleanedQuestion.condition = [];
    }

    return cleanedQuestion;
  };

  const handleAddQuestion = async () => {
    if (!selectedQuestion) return;

    try {
      setLoading(true);
      
      let cleanedQuestion = cleanQuestionObject(selectedQuestion);
      
      const isNewQuestion = selectedQuestion.isNew === true;
      
      if (isNewQuestion) {
        if (typeof cleanedQuestion.order !== 'number') {
          const sectionQuestions = organizedQuestions[selectedQuestion.section]?.[selectedQuestion.subSection] || [];
          let highestOrder = -1;
          
          sectionQuestions.forEach(question => {
            if (typeof question.order === 'number' && question.order > highestOrder) {
              highestOrder = question.order;
            }
          });
          
          cleanedQuestion.order = highestOrder + 1;
        }
        
        if (cleanedQuestion.custodians && cleanedQuestion.custodians.includes("IBKR")) {
          const { _id, ...questionWithoutId } = cleanedQuestion;
          cleanedQuestion = questionWithoutId;
        }
      }
      
      if (!cleanedQuestion.condition) {
        cleanedQuestion.condition = [];
      }
      
      if (cleanedQuestion.condition && cleanedQuestion.condition.length > 0) {
        const invalidConditions = cleanedQuestion.condition.filter(cond => 
          !cond.questionOrder || !cond.value
        );
        
        if (invalidConditions.length > 0) {
          throw new Error("All conditions must have both a question and an option selected. Please complete all condition fields.");
        }
        
        const emptyConditions = cleanedQuestion.condition.filter(cond => 
          !cond || Object.keys(cond).length === 0
        );
        
        if (emptyConditions.length > 0) {
          throw new Error("Please remove any empty condition fields before saving.");
        }
      }
      
      const method = isNewQuestion ? "POST" : "PATCH";
      
      const body = [cleanedQuestion];
      const response = await dispatch(CreateKYC({ 
        body, 
        method: method
      }));
      
      if (response?.payload?.statusCode === 201 || response?.payload?.statusCode === 200) {
        // Clear selected question first
        setSelectedQuestion(null);
        setActiveSection(null);
        
        // Wait a moment to ensure the API has processed the change
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Refresh the questions data
        await refreshQuestions();
      } else {
        console.error("Failed to process question:", response?.payload?.message);
        throw new Error(response?.payload?.message || "Failed to process question");
      }
    } catch (error) {
      console.error(`Error ${selectedQuestion.isNew ? 'adding' : 'updating'} question:`, error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveQuestion = async (id, isNew) => {
    setDeleteConfirmation({
      open: true,
      questionId: id,
      isNew: isNew
    });
  };

  const confirmDelete = async () => {
    const { questionId, isNew } = deleteConfirmation;
    
    try {
      setLoading(true);
      
      if (isNew) {
        const updatedQuestions = formik.values.questions.filter(
          (q) => q.id !== questionId
        );
        formik.setFieldValue("questions", updatedQuestions);
        
        const currentSection = Object.keys(organizedQuestions)[tabValue];
        const currentSubsection = 'Default'; // For now, we'll use Default subsection
        setOrganizedQuestions(prev => ({
          ...prev,
          [currentSection]: {
            ...prev[currentSection],
            [currentSubsection]: updatedQuestions
          }
        }));
      } else {
        const response = await dispatch(DeleteKYC(questionId));
        if (response?.payload?.statusCode === 200) {
          // Wait a moment for the API to process
          await new Promise(resolve => setTimeout(resolve, 500));
          await refreshQuestions();
        }
      }
    } catch (error) {
      console.error("Error deleting question:", error);
    } finally {
      setLoading(false);
      setDeleteConfirmation({ open: false, questionId: null, isNew: false });
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmation({ open: false, questionId: null, isNew: false });
  };

  const handleConfirmedAction = async () => {
    const { action, questionId } = confirmationDialog;
    setConfirmationDialog({ open: false, action: null, questionId: null });

    if (action === 'restore') {
      await handleRestoreQuestion(questionId);
    } else if (action === 'delete') {
      await handleDeleteQuestion(questionId);
    }
  };

  const handleRestoreQuestion = async (id) => {
    try {
      setLoading(true);
      
      const response = await dispatch(RestoreQuestion(id));
      if (response?.payload?.statusCode === 200) {
        // Wait a moment for the API to process
        await new Promise(resolve => setTimeout(resolve, 500));
        await refreshQuestions();
      }
    } catch (error) {
      console.error("Error restoring question:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteQuestion = async (id) => {
    try {
      setLoading(true);
      
      const response = await dispatch(DeleteKYC(id));
      if (response?.payload?.statusCode === 200) {
        // Wait a moment for the API to process
        await new Promise(resolve => setTimeout(resolve, 500));
        await refreshQuestions();
      }
    } catch (error) {
      console.error("Error deleting question:", error);
    } finally {
      setLoading(false);
    }
  };

  const moveQuestionUp = (section, subsection, index) => {
    if (index === 0) return;
    
    const sectionQuestions = [...(organizedQuestions[section]?.[subsection] || [])];
    
    const temp = sectionQuestions[index];
    sectionQuestions[index] = sectionQuestions[index - 1];
    sectionQuestions[index - 1] = temp;
    
    const updatedQuestions = sectionQuestions.map((question, idx) => ({
      ...question,
      order: idx,
      modified: true
    }));
    
    setOrganizedQuestions(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [subsection]: updatedQuestions
      }
    }));
    
    const currentSection = Object.keys(organizedQuestions)[tabValue];
    if (section === currentSection) {
      // Update formik with the current subsection's questions
      const currentSubsection = 'Default'; // For now, we'll use Default subsection
      formik.setFieldValue("questions", updatedQuestions);
    }
    
    setQuestionsReordered(true);
  };
  
  const moveQuestionDown = (section, subsection, index) => {
    const sectionQuestions = [...(organizedQuestions[section]?.[subsection] || [])];
    if (index === sectionQuestions.length - 1) return;
    
    const temp = sectionQuestions[index];
    sectionQuestions[index] = sectionQuestions[index + 1];
    sectionQuestions[index + 1] = temp;
    
    const updatedQuestions = sectionQuestions.map((question, idx) => ({
      ...question,
      order: idx,
      modified: true
    }));
    
    setOrganizedQuestions(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [subsection]: updatedQuestions
      }
    }));
    
    const currentSection = Object.keys(organizedQuestions)[tabValue];
    if (section === currentSection) {
      // Update formik with the current subsection's questions
      formik.setFieldValue("questions", updatedQuestions);
    }
    
    setQuestionsReordered(true);
  };

  const saveQuestionOrder = async (sectionName, subsectionName) => {
    try {
      setLoading(true);
      
      const sectionToUpdate = sectionName || Object.keys(organizedQuestions)[tabValue];
      const subsectionToUpdate = subsectionName || 'Default';
      
      if (!sectionToUpdate || !organizedQuestions[sectionToUpdate]) {
        console.error("Invalid section or no questions in this section");
        return;
      }
      
      const sectionQuestions = organizedQuestions[sectionToUpdate]?.[subsectionToUpdate] || [];
      const validIdQuestions = sectionQuestions.filter(question => {
        const hasValidId = question._id && /^[0-9a-fA-F]{24}$/.test(question._id);
        const hasInputType = question.inputType || question.type;
        return hasValidId && hasInputType;
      });
      
      if (validIdQuestions.length === 0) {
        console.error("No questions with valid MongoDB ObjectIDs found");
        setLoading(false);
        return;
      }

      if (!sectionQuestions || sectionQuestions.length === 0) {
        console.error("No questions found for section:", sectionToUpdate);
        return;
      }
      
      const questionsToUpdate = validIdQuestions.map((question, index) => {
        const updatedQuestion = {
          ...cleanQuestionObject(question),
          order: index,
          section: sectionToUpdate,
          subSection: subsectionToUpdate
        };
        
        if (!updatedQuestion.condition) {
          updatedQuestion.condition = [];
        }
        
        return updatedQuestion;
      });
      
      const updateResponse = await dispatch(CreateKYC({ 
        body: questionsToUpdate, 
        method: "PATCH" 
      }));
      
      if (updateResponse?.payload?.statusCode === 200) {
        // Wait a moment for the API to process
        await new Promise(resolve => setTimeout(resolve, 500));
        await refreshQuestions();
        setQuestionsReordered(false);
      } else {
        console.error("Failed to update question order:", updateResponse?.payload?.message);
      }
    } catch (error) {
      console.error("Error updating question order:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenMenu = (event, questionId) => {
    event.stopPropagation();
    setActiveQuestionId(questionId);
    setMenuAnchorEl(event.currentTarget);
  };
  
  const handleCloseMenu = (event) => {
    if (event) event.stopPropagation();
    setMenuAnchorEl(null);
  };
  
  const handleMoveToSection = async (question, currentSection, targetSection) => {
    if (targetSection === currentSection) {
      handleCloseMenu();
      return;
    }
    
    try {
      setLoading(true);
      
      const questionToUpdate = cleanQuestionObject(question);
      questionToUpdate.section = targetSection;
      questionToUpdate.order = (organizedQuestions[targetSection]?.length || 0);
      
      if (!questionToUpdate.condition) {
        questionToUpdate.condition = [];
      }
      
      const body = [questionToUpdate]
      
      const response = await dispatch(CreateKYC({ 
        body, 
        method: "PATCH" 
      }));
      
      if (response?.payload?.statusCode === 200) {
        // Wait a moment for the API to process
        await new Promise(resolve => setTimeout(resolve, 500));
        await refreshQuestions();
        handleCloseMenu();
      }
    } catch (error) {
      console.error("Error moving question to section:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSectionVisibilityToggle = async (sectionName, event) => {
    event.stopPropagation();
    
    try {
      setLoading(true);
      
      const sectionData = organizedQuestions[sectionName] || {};
      const allQuestions = Object.values(sectionData).flat();
      
      if (allQuestions.length === 0) return;
      
      const isCurrentlyDeleted = isSectionSoftDeleted(sectionName, organizedQuestions);
      const currentTime = new Date().toISOString();
      
      // Toggle delete status for all questions in the section
      const updatedQuestions = allQuestions.map(question => ({
        ...question,
        deletedAt: isCurrentlyDeleted ? null : currentTime,
        modified: true
      }));
      
      // Update the organizedQuestions state
      const updatedOrganizedQuestions = { ...organizedQuestions };
      Object.keys(sectionData).forEach(subsection => {
        const subsectionQuestions = sectionData[subsection];
        updatedOrganizedQuestions[sectionName][subsection] = subsectionQuestions.map(question => ({
          ...question,
          deletedAt: isCurrentlyDeleted ? null : currentTime,
          modified: true
        }));
      });
      
      setOrganizedQuestions(updatedOrganizedQuestions);
      
      // Update questions in the API
      const questionsToUpdate = updatedQuestions.map(question => cleanQuestionObject(question));
      
      const response = await dispatch(CreateKYC({ 
        body: questionsToUpdate, 
        method: "PATCH" 
      }));
      
      if (response?.payload?.statusCode === 200) {
        // Don't refresh questions to avoid section disappearing
        // The local state is already updated above
      } else {
        console.error("Failed to update section visibility:", response?.payload?.message);
      }
    } catch (error) {
      console.error("Error toggling section visibility:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSection = async (sectionName, event) => {
    event.stopPropagation();
    setDeleteSectionConfirmation({
      open: true,
      sectionName
    });
  };

  const confirmDeleteSection = async () => {
    try {
      setLoading(true);
      const response = await dispatch(DeleteSection(deleteSectionConfirmation.sectionName));
      
      if (response?.payload?.statusCode === 200) {
        // Clear selected question if it belongs to the deleted section
        if (selectedQuestion && selectedQuestion.section === deleteSectionConfirmation.sectionName) {
          setSelectedQuestion(null);
          setActiveSection(null);
        }
        
        // Wait a moment for the API to process
        await new Promise(resolve => setTimeout(resolve, 500));
        await refreshQuestions();
        
        const newSections = sections.filter(s => s !== deleteSectionConfirmation.sectionName);
        setSections(newSections);
        
        if (tabValue >= newSections.length) {
          setTabValue(Math.max(0, newSections.length - 1));
        }
      }
    } catch (error) {
      console.error("Error deleting section:", error);
    } finally {
      setLoading(false);
      setDeleteSectionConfirmation({ open: false, sectionName: null });
    }
  };

  const isSectionSoftDeleted = (sectionName, organizedQuestions) => {
    const sectionData = organizedQuestions[sectionName] || {};
    if (typeof sectionData !== 'object' || Object.keys(sectionData).length === 0) return false;
    
    // Check if all subsections are empty or all questions in all subsections are soft deleted
    const allSubsections = Object.values(sectionData);
    if (allSubsections.length === 0) return false;
    
    return allSubsections.every(subsectionQuestions => {
      if (!Array.isArray(subsectionQuestions) || subsectionQuestions.length === 0) return true;
      return subsectionQuestions.every(question => question.deletedAt !== null && question.deletedAt !== undefined);
    });
  };

  const isSubsectionSoftDeleted = (sectionName, subsectionName, organizedQuestions) => {
    const subsectionQuestions = organizedQuestions[sectionName]?.[subsectionName] || [];
    if (subsectionQuestions.length === 0) return false;
    return subsectionQuestions.every(question => question.deletedAt !== null && question.deletedAt !== undefined);
  };

  const renderQuestionRow = (question, section, subsection, index) => {
    const questionId = question._id || question.id || `${section}-${subsection}-${index}`;
    
    return (
      <Box
        key={questionId}
        onClick={() => handleQuestionSelect(question, section)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          padding: '8px 0',
          borderBottom: '1px solid #f3f4f6',
          cursor: 'pointer',
          transition: 'background-color 0.2s ease',
          opacity: question.deletedAt ? 0.6 : 1,
          backgroundColor: question.deletedAt ? '#fef2f2' : 'transparent',
          borderLeft: question.deletedAt ? '3px solid #ef4444' : 'none',
          paddingLeft: question.deletedAt ? '12px' : '0px',
          '&:hover': {
            backgroundColor: question.deletedAt ? '#fee2e2' : '#f9fafb'
          },
          '&:last-child': {
            borderBottom: 'none'
          }
        }}
      >
        {/* Drag Handle - 6 dots in 2x3 grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 4px)',
            gridTemplateRows: 'repeat(3, 4px)',
            gap: '2px',
            width: '12px',
            height: '14px',
            marginRight: '16px',
            cursor: 'grab'
          }}
        >
          {[...Array(6)].map((_, i) => (
            <Box
              key={i}
              sx={{
                width: '3px',
                height: '3px',
                backgroundColor: '#d1d5db',
                borderRadius: '1px'
              }}
            />
          ))}
        </Box>

        {/* Question Text */}
        <Box sx={{ flex: 1, marginRight: '16px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography
              sx={{
                fontSize: '14px',
                color: question.deletedAt ? '#9ca3af' : '#374151',
                fontWeight: 400,
                lineHeight: '20px',
                textDecoration: question.deletedAt ? 'line-through' : 'none'
              }}
            >
              {question.text || question.label}
            </Typography>
            {question.deletedAt && (
              <Box sx={{
                backgroundColor: '#ef4444',
                color: '#ffffff',
                fontSize: '10px',
                fontWeight: 600,
                padding: '2px 6px',
                borderRadius: '4px',
                textTransform: 'uppercase'
              }}>
                Deleted
              </Box>
            )}
          </Box>
        </Box>

        {/* Required/Optional Dropdown */}
        <Select
          value={question.required ? "Required" : "Optional"}
          size="small"
          onClick={(e) => e.stopPropagation()}
          sx={{
            width: '100px',
            marginRight: '12px',
            height: '32px',
            fontSize: '12px',
            '& .MuiOutlinedInput-root': {
              borderRadius: '6px',
              borderColor: '#e5e7eb'
            }
          }}
        >
          <MenuItem value="Required">Required</MenuItem>
          <MenuItem value="Optional">Optional</MenuItem>
        </Select>

        {/* Question Type Dropdown */}
        <Select
          value={question.type || question.inputType || 'File Upload'}
          size="small"
          onClick={(e) => e.stopPropagation()}
          sx={{
            width: '120px',
            marginRight: '12px',
            height: '32px',
            fontSize: '12px',
            '& .MuiOutlinedInput-root': {
              borderRadius: '6px',
              borderColor: '#e5e7eb'
            }
          }}
        >
          {questionTypes.map((type) => (
            <MenuItem key={type.value} value={type.value}>
              {type.label}
            </MenuItem>
          ))}
        </Select>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <IconButton 
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              moveQuestionUp(section, subsection, index);
            }}
            disabled={index === 0}
            sx={{
              width: '24px',
              height: '24px',
              color: index === 0 ? '#d1d5db' : '#9ca3af',
              '&:hover': {
                color: index === 0 ? '#d1d5db' : '#6b7280',
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            <ArrowUpwardIcon sx={{ fontSize: 14 }} />
          </IconButton>

          <IconButton 
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              moveQuestionDown(section, subsection, index);
            }}
            disabled={index === (organizedQuestions[section]?.[subsection]?.length || 0) - 1}
            sx={{
              width: '24px',
              height: '24px',
              color: index === (organizedQuestions[section]?.[subsection]?.length || 0) - 1 ? '#d1d5db' : '#9ca3af',
              '&:hover': {
                color: index === (organizedQuestions[section]?.[subsection]?.length || 0) - 1 ? '#d1d5db' : '#6b7280',
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            <ArrowDownwardIcon sx={{ fontSize: 14 }} />
          </IconButton>

          <IconButton 
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              if (question.deletedAt) {
                setConfirmationDialog({
                  open: true,
                  action: 'restore',
                  questionId: questionId
                });
              } else {
                handleRemoveQuestion(questionId, false);
              }
            }}
            sx={{
              width: '24px',
              height: '24px',
              color: question.deletedAt ? '#ef4444' : '#9ca3af',
              '&:hover': {
                color: question.deletedAt ? '#dc2626' : '#6b7280',
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            {question.deletedAt ? (
              <VisibilityOffIcon sx={{ fontSize: 14 }} />
            ) : (
              <VisibilityIcon sx={{ fontSize: 14 }} />
            )}
          </IconButton>

          <IconButton 
            size="small"
            onClick={(e) => handleOpenMenu(e, questionId)}
            sx={{
              width: '24px',
              height: '24px',
              color: '#9ca3af',
              '&:hover': {
                color: '#6b7280',
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            <OpenWithIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </Box>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{
        width: '100%',
        backgroundColor: '#f8fafc',
        minHeight: '100vh',
        padding: '24px'
      }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography sx={{
            color: '#1f2937',
            fontSize: '28px',
            fontWeight: 700,
            marginBottom: '8px'
          }}>
            KYC Management
          </Typography>
          <Typography sx={{
            color: '#6b7280',
            fontSize: '16px'
          }}>
            Create and manage your KYC forms with custom sections and questions
          </Typography>
        </Box>

        {/* Loading indicator */}
        {loading && (
          <Box 
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 9999
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <img src={HeaderIcon} alt="Loading" style={{ width: '80px', marginBottom: '20px' }} />
              <Typography variant="h6" color="primary">Loading...</Typography>
            </Box>
          </Box>
        )}

        {/* Custodian Selection */}
        <Box sx={{ mb: 4 }}>
          <Typography sx={{
            fontSize: '18px',
            fontWeight: 600,
            color: '#1f2937',
            marginBottom: '16px'
          }}>
            Select Custodian
          </Typography>
          <Box sx={{
            display: 'flex',
            gap: '16px',
            flexWrap: 'wrap'
          }}>
            {custodianData
              .filter(custodian => custodian.type === "INTERNAL")
              .map((custodian) => (
                <Box
                  key={custodian.id}
                  onClick={() => handleCustodianClick(custodian.name)}
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: selectedCustodians.includes(custodian.name) ? '#eff6ff' : '#ffffff',
                    border: selectedCustodians.includes(custodian.name) ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                    borderRadius: '12px',
                    padding: '20px 16px',
                    minWidth: '200px',
                    height: '80px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: '#3b82f6',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.15)'
                    }
                  }}
                >
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    color: '#3b82f6'
                  }}>
                    <img 
                      src={HeaderIcon} 
                      alt={custodian.name}
                      style={{ width: '24px', height: '24px' }}
                    />
                  </Box>

                  <Typography sx={{
                    color: '#1f2937',
                    fontSize: '14px',
                    fontWeight: 500,
                    flex: 1
                  }}>
                    {custodian.name}
                  </Typography>

                  <Box sx={{
                    width: '20px',
                    height: '20px',
                    border: selectedCustodians.includes(custodian.name) ? '2px solid #3b82f6' : '2px solid #d1d5db',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {selectedCustodians.includes(custodian.name) && (
                      <Box sx={{
                        width: '10px',
                        height: '10px',
                        backgroundColor: '#3b82f6',
                        borderRadius: '50%'
                      }} />
                    )}
                  </Box>
                </Box>
            ))}

            {custodianData
              .filter(custodian => custodian.type === "EXTERNAL" && custodian.apisupported)
              .map((custodian) => (
                <Box
                  key={custodian.id}
                  onClick={() => handleCustodianClick(custodian.name)}
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: selectedCustodians.includes(custodian.name) ? '#eff6ff' : '#ffffff',
                    border: selectedCustodians.includes(custodian.name) ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                    borderRadius: '12px',
                    padding: '20px 16px',
                    minWidth: '200px',
                    height: '80px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: '#3b82f6',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.15)'
                    }
                  }}
                >
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    color: '#3b82f6'
                  }}>
                    <img 
                      src={HeaderIcon} 
                      alt={custodian.name}
                      style={{ width: '24px', height: '24px' }}
                    />
                  </Box>

                  <Typography sx={{
                    color: '#1f2937',
                    fontSize: '14px',
                    fontWeight: 500,
                    flex: 1
                  }}>
                    {custodian.name}
                  </Typography>

                  <Box sx={{
                    width: '20px',
                    height: '20px',
                    border: selectedCustodians.includes(custodian.name) ? '2px solid #3b82f6' : '2px solid #d1d5db',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {selectedCustodians.includes(custodian.name) && (
                      <Box sx={{
                        width: '10px',
                        height: '10px',
                        backgroundColor: '#3b82f6',
                        borderRadius: '50%'
                      }} />
                    )}
                  </Box>
                </Box>
            ))}
          </Box>
        </Box>

        {/* Role Selection */}
        {showRoleSection && (
          <Paper sx={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e5e7eb',
            marginBottom: '24px',
            padding: '24px'
          }}>
            <Typography sx={{
              fontSize: '18px',
              fontWeight: 600,
              color: '#1f2937',
              marginBottom: '20px'
            }}>
              Select Role
            </Typography>

            <Box sx={{
              display: 'flex',
              gap: '16px'
            }}>
              <Button
                onClick={() => handleUserTypeChange({ target: { value: 'individual' }})}
                variant={selectedUserType === 'individual' ? 'contained' : 'outlined'}
                sx={{
                  textTransform: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 500,
                  backgroundColor: selectedUserType === 'individual' ? '#3b82f6' : 'transparent',
                  borderColor: '#3b82f6',
                  color: selectedUserType === 'individual' ? '#ffffff' : '#3b82f6',
                  '&:hover': {
                    backgroundColor: selectedUserType === 'individual' ? '#2563eb' : '#eff6ff',
                    borderColor: '#2563eb'
                  }
                }}
              >
                Individual
              </Button>
              <Button
                onClick={() => handleUserTypeChange({ target: { value: 'corporate' }})}
                variant={selectedUserType === 'corporate' ? 'contained' : 'outlined'}
                sx={{
                  textTransform: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 500,
                  backgroundColor: selectedUserType === 'corporate' ? '#3b82f6' : 'transparent',
                  borderColor: '#3b82f6',
                  color: selectedUserType === 'corporate' ? '#ffffff' : '#3b82f6',
                  '&:hover': {
                    backgroundColor: selectedUserType === 'corporate' ? '#2563eb' : '#eff6ff',
                    borderColor: '#2563eb'
                  }
                }}
              >
                Corporate
              </Button>
              <Button
                onClick={() => handleUserTypeChange({ target: { value: 'trust' }})}
                variant={selectedUserType === 'trust' ? 'contained' : 'outlined'}
                sx={{
                  textTransform: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 500,
                  backgroundColor: selectedUserType === 'trust' ? '#3b82f6' : 'transparent',
                  borderColor: '#3b82f6',
                  color: selectedUserType === 'trust' ? '#ffffff' : '#3b82f6',
                  '&:hover': {
                    backgroundColor: selectedUserType === 'trust' ? '#2563eb' : '#eff6ff',
                    borderColor: '#2563eb'
                  }
                }}
              >
                Trust
              </Button>
            </Box>
          </Paper>
        )}


        {/* Sections */}
        {selectedUserType && (
          <Box sx={{ width: "100%" }}>
            {sections.map((section, index) => (
              <Accordion
                key={index}
                defaultExpanded={true}
                sx={{
                  backgroundColor: '#ffffff',
                  borderRadius: '8px !important',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #e5e7eb',
                  marginBottom: '16px',
                  '&::before': {
                    display: 'none'
                  }
                }}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMore sx={{ color: '#6b7280' }} />}
                  sx={{
                    padding: '16px 24px',
                    borderBottom: '1px solid #f3f4f6',
                    '& .MuiAccordionSummary-content': {
                      margin: 0,
                      alignItems: 'center'
                    }
                  }}
                >
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    width: '100%',
                    marginRight: '16px'
                  }}>
                    <Typography sx={{ 
                      fontWeight: 600, 
                      fontSize: '16px',
                      color: '#1f2937'
                    }}>
                      Section {index + 1}: {section}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Button
                        startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddNewQuestion(section, 'Default');
                        }}
                        sx={{
                          fontSize: '12px',
                          fontWeight: 500,
                          textTransform: 'none',
                          color: '#3b82f6',
                          backgroundColor: 'transparent',
                          padding: '4px 8px',
                          minWidth: 'auto',
                          '&:hover': {
                            backgroundColor: '#eff6ff'
                          }
                        }}
                      >
                        Add Question
                      </Button>

                      <Button
                        startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddSubsection(section);
                        }}
                        sx={{
                          fontSize: '12px',
                          fontWeight: 500,
                          textTransform: 'none',
                          color: '#3b82f6',
                          backgroundColor: 'transparent',
                          padding: '4px 8px',
                          minWidth: 'auto',
                          '&:hover': {
                            backgroundColor: '#eff6ff'
                          }
                        }}
                      >
                        Add Sub-Section
                      </Button>

                      {/* Section Visibility/Delete Toggle */}
                      <IconButton
                        onClick={(e) => handleSectionVisibilityToggle(section, e)}
                        sx={{
                          width: '28px',
                          height: '28px',
                          color: isSectionSoftDeleted(section, organizedQuestions) ? '#ef4444' : '#6b7280',
                          '&:hover': {
                            color: isSectionSoftDeleted(section, organizedQuestions) ? '#dc2626' : '#374151',
                            backgroundColor: isSectionSoftDeleted(section, organizedQuestions) ? 'rgba(239, 68, 68, 0.04)' : 'rgba(0, 0, 0, 0.04)'
                          }
                        }}
                      >
                        {isSectionSoftDeleted(section, organizedQuestions) ? (
                          <VisibilityOffIcon sx={{ fontSize: 16 }} />
                        ) : (
                          <VisibilityIcon sx={{ fontSize: 16 }} />
                        )}
                      </IconButton>
                    </Box>
                  </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ padding: '0 24px 24px 24px' }}>
                  <Box sx={{ display: 'flex', gap: '24px' }}>
                    <Box sx={{ flex: 1 }}>
                      {/* Check if there are custom subsections (excluding Default) */}
                      {(() => {
                        const customSubsections = subsections[section]?.filter(sub => sub !== 'Default') || [];
                        const hasCustomSubsections = customSubsections.length > 0;
                        const defaultQuestions = organizedQuestions[section]?.['Default'] || [];
                        
                        // If no custom subsections, show questions directly in section
                        if (!hasCustomSubsections) {
                          return (
                            <Box>
                              {/* Questions directly in section */}
                              {defaultQuestions.length > 0 ? (
                                <Box>
                                  {defaultQuestions.map((question, idx) => 
                                    renderQuestionRow(question, section, 'Default', idx)
                                  )}
                                  
                                  {/* Update Order Button for Section Questions */}
                                  {questionsReordered && (
                                    <Box sx={{ 
                                      display: 'flex', 
                                      justifyContent: 'flex-end', 
                                      mt: 2,
                                      gap: 1
                                    }}>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => saveQuestionOrder(section, 'Default')}
                                        disabled={loading}
                                        sx={{
                                          fontSize: '11px',
                                          textTransform: 'none',
                                          borderColor: '#d1d5db',
                                          color: '#6b7280',
                                          '&:hover': {
                                            borderColor: '#9ca3af',
                                            backgroundColor: '#f9fafb'
                                          }
                                        }}
                                      >
                                        {loading ? 'Saving...' : 'Save Order'}
                                      </Button>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => setQuestionsReordered(false)}
                                        sx={{
                                          fontSize: '11px',
                                          textTransform: 'none',
                                          borderColor: '#d1d5db',
                                          color: '#6b7280',
                                          '&:hover': {
                                            borderColor: '#9ca3af',
                                            backgroundColor: '#f9fafb'
                                          }
                                        }}
                                      >
                                        Cancel
                                      </Button>
                                    </Box>
                                  )}
                                </Box>
                              ) : (
                                <Box sx={{ 
                                  textAlign: 'center', 
                                  py: 6,
                                  color: '#6b7280'
                                }}>
                                  <Typography sx={{ fontSize: '14px' }}>
                                    No questions added yet. Click "Add Question" to get started.
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          );
                        }
                        
                        // If there are custom subsections, show them with Default subsection
                        return (
                          <>
                            {/* Default subsection (only show when there are custom subsections) */}
                            {defaultQuestions.length > 0 && (
                              <Box sx={{
                                backgroundColor: '#f9fafb',
                                borderRadius: '8px',
                                border: '1px solid #e5e7eb',
                                padding: '16px',
                                marginBottom: '16px',
                                position: 'relative'
                              }}>
                                {/* Default Subsection Header */}
                                <Box sx={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  alignItems: 'center',
                                  marginBottom: '12px'
                                }}>
                                  <Typography sx={{ 
                                    fontWeight: 600, 
                                    fontSize: '14px',
                                    color: '#1f2937'
                                  }}>
                                    Default
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                      startIcon={<AddIcon sx={{ fontSize: 12 }} />}
                                      onClick={() => handleAddNewQuestion(section, 'Default')}
                                      sx={{
                                        fontSize: '11px',
                                        textTransform: 'none',
                                        backgroundColor: '#ffffff',
                                        color: '#374151',
                                        border: '1px solid #d1d5db',
                                        '&:hover': {
                                          backgroundColor: '#f3f4f6',
                                          borderColor: '#9ca3af'
                                        }
                                      }}
                                    >
                                      Add Question
                                    </Button>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => handleSubsectionVisibilityToggle(section, 'Default', e)}
                                      sx={{
                                        color: '#6b7280',
                                        '&:hover': {
                                          color: '#374151',
                                          backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                        }
                                      }}
                                    >
                                      {isSubsectionSoftDeleted(section, 'Default', organizedQuestions) ? (
                                        <VisibilityOffIcon sx={{ fontSize: 16 }} />
                                      ) : (
                                        <VisibilityIcon sx={{ fontSize: 16 }} />
                                      )}
                                    </IconButton>
                                  </Box>
                                </Box>

                                {/* Questions in Default subsection */}
                                <Box>
                                  {defaultQuestions.map((question, idx) => 
                                    renderQuestionRow(question, section, 'Default', idx)
                                  )}
                                  
                                  {/* Update Order Button for Default subsection */}
                                  {questionsReordered && (
                                    <Box sx={{ 
                                      display: 'flex', 
                                      justifyContent: 'flex-end', 
                                      mt: 2,
                                      gap: 1
                                    }}>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => saveQuestionOrder(section, 'Default')}
                                        disabled={loading}
                                        sx={{
                                          fontSize: '11px',
                                          textTransform: 'none',
                                          borderColor: '#d1d5db',
                                          color: '#6b7280',
                                          '&:hover': {
                                            borderColor: '#9ca3af',
                                            backgroundColor: '#f9fafb'
                                          }
                                        }}
                                      >
                                        {loading ? 'Saving...' : 'Save Order'}
                                      </Button>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => setQuestionsReordered(false)}
                                        sx={{
                                          fontSize: '11px',
                                          textTransform: 'none',
                                          borderColor: '#d1d5db',
                                          color: '#6b7280',
                                          '&:hover': {
                                            borderColor: '#9ca3af',
                                            backgroundColor: '#f9fafb'
                                          }
                                        }}
                                      >
                                        Cancel
                                      </Button>
                                    </Box>
                                  )}
                                </Box>
                              </Box>
                            )}
                          </>
                        );
                      })()}
                    
                      {/* Custom Subsection Cards */}
                      {subsections[section] && subsections[section].filter(sub => sub !== 'Default').length > 0 ? (
                        subsections[section].filter(sub => sub !== 'Default').map((subsection, subIndex) => (
                          <Box
                            key={`${section}-${subsection}-${subIndex}`}
                            sx={{
                              backgroundColor: '#f9fafb',
                              borderRadius: '8px',
                              border: '1px solid #e5e7eb',
                              padding: '16px',
                              marginBottom: '16px',
                              position: 'relative'
                            }}
                          >
                            {/* Subsection Header */}
                            <Box sx={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              marginBottom: '12px'
                            }}>
                              <Typography sx={{ 
                                fontWeight: 600, 
                                fontSize: '14px',
                                color: '#1f2937'
                              }}>
                                {subsection}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                  startIcon={<AddIcon sx={{ fontSize: 12 }} />}
                                  onClick={() => handleAddNewQuestion(section, subsection)}
                                  sx={{
                                    fontSize: '11px',
                                    textTransform: 'none',
                                    backgroundColor: '#ffffff',
                                    color: '#374151',
                                    border: '1px solid #d1d5db',
                                    '&:hover': {
                                      backgroundColor: '#f3f4f6',
                                      borderColor: '#9ca3af'
                                    }
                                  }}
                                >
                                  Add Question
                                </Button>
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleSubsectionVisibilityToggle(section, subsection, e)}
                                  sx={{
                                    color: '#6b7280',
                                    '&:hover': {
                                      color: '#374151',
                                      backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                    }
                                  }}
                                >
                                  {isSubsectionSoftDeleted(section, subsection, organizedQuestions) ? (
                                    <VisibilityOffIcon sx={{ fontSize: 16 }} />
                                  ) : (
                                    <VisibilityIcon sx={{ fontSize: 16 }} />
                                  )}
                                </IconButton>
                              </Box>
                            </Box>

                            {/* Questions in Subsection */}
                            <Box>
                              {(() => {
                                const subsectionQuestions = organizedQuestions[section]?.[subsection] || [];
                                
                                return subsectionQuestions.length === 0 ? (
                                <Box sx={{ 
                                  textAlign: 'center', 
                                  py: 3,
                                  color: '#6b7280'
                                }}>
                                  <Typography sx={{ fontSize: '12px' }}>
                                    No questions in this subsection yet.
                                  </Typography>
                                </Box>
                              ) : (
                                <Box>
                                    {subsectionQuestions.map((question, idx) => 
                                    renderQuestionRow(question, section, subsection, idx)
                                  )}
                                  
                                  {/* Update Order Button for Subsection */}
                                  {questionsReordered && (
                                    <Box sx={{ 
                                      display: 'flex', 
                                      justifyContent: 'flex-end', 
                                      mt: 2,
                                      gap: 1
                                    }}>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => saveQuestionOrder(section, subsection)}
                                        disabled={loading}
                                        sx={{
                                          fontSize: '11px',
                                          textTransform: 'none',
                                          borderColor: '#d1d5db',
                                          color: '#6b7280',
                                          '&:hover': {
                                            borderColor: '#9ca3af',
                                            backgroundColor: '#f9fafb'
                                          }
                                        }}
                                      >
                                        {loading ? 'Saving...' : 'Save Order'}
                                      </Button>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => setQuestionsReordered(false)}
                                        sx={{
                                          fontSize: '11px',
                                          textTransform: 'none',
                                          borderColor: '#d1d5db',
                                          color: '#6b7280',
                                          '&:hover': {
                                            borderColor: '#9ca3af',
                                            backgroundColor: '#f9fafb'
                                          }
                                        }}
                                      >
                                        Cancel
                                      </Button>
                                    </Box>
                                  )}
                                </Box>
                                );
                              })()}
                            </Box>
                          </Box>
                        ))
                      ) : null}

                      {/* Add Subsection Input */}
                      {isAddingSubsection && activeSubsection === section && (
                        <Box sx={{
                          backgroundColor: '#ffffff',
                          borderRadius: '8px',
                          border: '2px dashed #d1d5db',
                          padding: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginBottom: '16px'
                        }}>
                          <TextField
                            size="small"
                            placeholder="Enter subsection name"
                            value={newSubsectionName}
                            onChange={(e) => setNewSubsectionName(e.target.value)}
                            onKeyPress={handleSubsectionNameSubmit}
                            autoFocus
                            sx={{
                              flex: 1,
                              '& .MuiOutlinedInput-root': {
                                fontSize: '12px'
                              }
                            }}
                          />
                          <Button
                            size="small"
                            onClick={() => {
                              setIsAddingSubsection(false);
                              setNewSubsectionName('');
                              setActiveSubsection(null);
                            }}
                            sx={{
                              fontSize: '11px',
                              textTransform: 'none',
                              color: '#6b7280'
                            }}
                          >
                            Cancel
                          </Button>
                        </Box>
                      )}
                    </Box>

                    {/* Question Editor Panel */}
                    {selectedQuestion && activeSection === section && (
                      <Box sx={{
                        width: '400px',
                        backgroundColor: '#ffffff',
                        borderRadius: '12px',
                        border: '1px solid #e5e7eb',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        height: '80vh', // Fixed height
                        maxHeight: '800px',
                        minHeight: '600px',
                        position: 'sticky',
                        top: '20px',
                        display: 'flex',
                        flexDirection: 'column'
                      }}>
                        <Box sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '16px 20px',
                          borderBottom: '1px solid #f3f4f6',
                          backgroundColor: '#f9fafb',
                          borderRadius: '12px 12px 0 0',
                          flexShrink: 0 // Prevent header from shrinking
                        }}>
                          <Typography sx={{
                            fontSize: '16px',
                            fontWeight: 600,
                            color: '#1f2937'
                          }}>
                            {selectedQuestion.isNew ? 'Add Question' : 'Update Question'}
                          </Typography>
                          <IconButton 
                            onClick={() => setSelectedQuestion(null)}
                            sx={{
                              width: '32px',
                              height: '32px',
                              color: '#6b7280',
                              '&:hover': {
                                color: '#374151',
                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                              }
                            }}
                          >
                            <CloseIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Box>

                        <Box sx={{ 
                          padding: '20px',
                          backgroundColor: '#ffffff',
                          flex: 1, // Take remaining space
                          overflowY: 'auto', // Enable vertical scrolling
                          overflowX: 'hidden', // Prevent horizontal scrolling
                          '&::-webkit-scrollbar': {
                            width: '6px',
                          },
                          '&::-webkit-scrollbar-track': {
                            background: '#f1f1f1',
                            borderRadius: '3px',
                          },
                          '&::-webkit-scrollbar-thumb': {
                            background: '#c1c1c1',
                            borderRadius: '3px',
                            '&:hover': {
                              background: '#a8a8a8',
                            },
                          },
                        }}>
                          {/* Question Preview - Read Only */}
                          <TextField
                            fullWidth
                            value={selectedQuestion.text || ''}
                            placeholder="Questions Preview"
                            variant="standard"
                            InputProps={{
                              readOnly: true,
                            }}
                            sx={{ 
                              mb: 2,
                              '& .MuiInput-underline:before': {
                                borderBottomColor: '#e5e7eb'
                              },
                              '& .MuiInput-underline:after': {
                                borderBottomColor: '#3b82f6'
                              },
                              '& .MuiInputBase-input': {
                                fontSize: '16px',
                                color: selectedQuestion.text ? '#374151' : '#9ca3af',
                                cursor: 'default'
                              }
                            }}
                          />

                          {/* Required Checkbox */}
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: '20px' 
                          }}>
                            <Typography sx={{ fontSize: '14px', color: '#6b7280' }}>
                              Mark as Required
                            </Typography>
                            <Checkbox
                              checked={selectedQuestion.required || false}
                              onChange={(e) => handleQuestionChange(selectedQuestion.id, 'required', e.target.checked)}
                              sx={{
                                color: '#d1d5db',
                                '&.Mui-checked': {
                                  color: '#3b82f6',
                                }
                              }}
                            />
                          </Box>

                          {/* Question Type Selection */}
                          <Box sx={{ marginBottom: '20px' }}>
                            <Typography sx={{
                              fontSize: '14px',
                              color: '#6b7280',
                              marginBottom: '8px'
                            }}>
                              Select response type
                            </Typography>
                            <Select
                              fullWidth
                              value={selectedQuestion?.type || ''}
                              onChange={(e) => handleQuestionChange(selectedQuestion.id, 'type', e.target.value)}
                              displayEmpty
                              sx={{
                                backgroundColor: '#f9fafb',
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '8px',
                                  borderColor: '#e5e7eb'
                                },
                                '& .MuiSelect-select': {
                                  padding: '12px 14px',
                                  fontSize: '14px',
                                  color: selectedQuestion?.type ? '#374151' : '#9ca3af'
                                }
                              }}
                            >
                              <MenuItem value="" disabled>
                                <span style={{ color: '#9ca3af' }}>Select</span>
                              </MenuItem>
                              {questionTypes.map((type) => (
                                <MenuItem key={type.value} value={type.value}>
                                  {type.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </Box>

                          {/* Conditional Question Toggle */}
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Typography sx={{ fontSize: '14px', color: '#333333' }}>
                              Conditional Question
                            </Typography>
                            <Switch
                              checked={selectedQuestion.conditionalEnabled || false}
                              onChange={(e) => handleQuestionChange(selectedQuestion.id, 'conditionalEnabled', e.target.checked)}
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: '#3b82f6',
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                  backgroundColor: '#3b82f6',
                                }
                              }}
                            />
                          </Box>

                                                    {/* Conditional Questions UI */}
                          {selectedQuestion.conditionalEnabled && (
                            <Box sx={{
                              marginBottom: '20px',
                              padding: '16px',
                              backgroundColor: '#f8fafc',
                              border: '1px solid #e5e7eb',
                              borderRadius: '12px'
                            }}>
                              <Typography sx={{
                                color: '#3b82f6',
                                fontSize: '18px',
                                fontWeight: 600,
                                marginBottom: '16px'
                              }}>
                                Conditional Questions
                              </Typography>
                              
                              <Box sx={{
                                padding: '16px',
                                backgroundColor: '#eff6ff',
                                borderRadius: '8px',
                                borderLeft: '4px solid #3b82f6',
                                marginBottom: '20px'
                              }}>
                                <Typography sx={{ 
                                  color: '#6b7280',
                                  fontSize: '14px',
                                  lineHeight: 1.5
                                }}>
                                  Add conditions based on answers to other questions. Only questions with dropdown, radio, checkbox, or boolean types can be referenced in conditions. <strong>This is optional.</strong>
                                </Typography>
                              </Box>
                              
                              {selectedQuestion.condition?.map((condition, index) => (
                                <Box 
                                  key={index} 
                                  sx={{
                                    marginBottom: '16px',
                                    padding: '16px',
                                    backgroundColor: '#ffffff',
                                    borderRadius: '12px',
                                    border: '2px solid #4ade80',
                                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                                  }}
                                >
                                  {/* Grid layout: 2 rows, 2 columns */}
                                  <Box sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr auto',
                                    gridTemplateRows: '1fr 1fr',
                                    gap: '12px',
                                    alignItems: 'center'
                                  }}>
                                    {/* First dropdown - spans first row, first column */}
                                    <Select
                                      value={condition.questionOrder || ''}
                                      onChange={(e) => handleConditionalChange(selectedQuestion.id, index, 'questionOrder', e.target.value)}
                                      displayEmpty
                                      sx={{ 
                                        gridColumn: '1',
                                        gridRow: '1',
                                        backgroundColor: '#f9fafb',
                                        '& .MuiOutlinedInput-root': {
                                          borderRadius: '8px',
                                          borderColor: '#e5e7eb'
                                        },
                                        '& .MuiSelect-select': {
                                          padding: '12px 14px',
                                          fontSize: '14px'
                                        }
                                      }}
                                    >
                                      <MenuItem value="" disabled>
                                        <span style={{ color: '#9ca3af' }}>Intended use of Coinbase</span>
                                      </MenuItem>
                                      {getConditionalQuestions(organizedQuestions).length > 0 ? (
                                        getConditionalQuestions(organizedQuestions).map((question) => (
                                          <MenuItem key={question._id || question.id} value={question._id || question.id}>
                                            {question.text || question.label || `Question ${(question.order || 0) + 1}`}
                                          </MenuItem>
                                        ))
                                      ) : (
                                        <MenuItem disabled>
                                          No option-based questions available
                                        </MenuItem>
                                      )}
                                    </Select>
                                    
                                    {/* Second dropdown - spans second row, first column */}
                                    <Select
                                      value={condition.value || ''}
                                      onChange={(e) => handleConditionalChange(selectedQuestion.id, index, 'value', e.target.value)}
                                      displayEmpty
                                      disabled={!condition.questionOrder}
                                      sx={{ 
                                        gridColumn: '1',
                                        gridRow: '2',
                                        backgroundColor: '#f9fafb',
                                        '& .MuiOutlinedInput-root': {
                                          borderRadius: '8px',
                                          borderColor: '#e5e7eb'
                                        },
                                        '& .MuiSelect-select': {
                                          padding: '12px 14px',
                                          fontSize: '14px'
                                        }
                                      }}
                                    >
                                      <MenuItem value="" disabled>
                                        <span style={{ color: '#9ca3af' }}>Trading</span>
                                      </MenuItem>
                                      {(() => {
                                        if (condition.questionOrder) {
                                          const selectedQuestionForCondition = getConditionalQuestions(organizedQuestions)
                                            .find(q => (q._id || q.id) === condition.questionOrder);
                                          
                                          if (selectedQuestionForCondition && selectedQuestionForCondition.options) {
                                            return selectedQuestionForCondition.options.map((option, optIndex) => (
                                              <MenuItem key={optIndex} value={option}>
                                                {option}
                                              </MenuItem>
                                            ));
                                          }
                                        }
                                        return null;
                                      })()}
                                    </Select>

                                    {/* Delete button - spans both rows, positioned between the fields */}
                                    <IconButton 
                                      onClick={() => removeConditionalQuestion(selectedQuestion.id, index)}
                                      sx={{ 
                                        gridColumn: '3',
                                        gridRow: '1 / 3', // Spans both rows
                                        alignSelf: 'center',
                                        color: '#ef4444',
                                        width: '36px',
                                        height: '36px',
                                        backgroundColor: '#fef2f2',
                                        border: '1px solid #fecaca',
                                        borderRadius: '6px',
                                        '&:hover': {
                                          backgroundColor: '#fee2e2',
                                          color: '#dc2626'
                                        }
                                      }}
                                    >
                                      <DeleteIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                  </Box>
                                </Box>
                              ))}
                              
                              <Button
                                onClick={() => addConditionalQuestion(selectedQuestion.id)}
                                variant="outlined"
                                sx={{ 
                                  width: '100%',
                                  height: '48px',
                                  border: '2px solid #3b82f6',
                                  color: '#3b82f6',
                                  fontWeight: 600,
                                  fontSize: '14px',
                                  textTransform: 'none',
                                  borderRadius: '8px',
                                  backgroundColor: '#ffffff',
                                  '&:hover': {
                                    backgroundColor: '#eff6ff',
                                    borderColor: '#2563eb'
                                  }
                                }}
                              >
                                + ADD MORE CONDITIONS
                              </Button>
                            </Box>
                          )}

                          {/* Type-specific fields for Text/Number */}
                          {isTextOrNumberType(selectedQuestion.type) && (
                            <>
                              {/* Text Input Field - Where user types the question */}
                              <Box sx={{ marginBottom: '20px' }}>
                                <TextField
                                  fullWidth
                                  multiline
                                  rows={4}
                                  value={selectedQuestion.textInput || ''}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    handleQuestionChange(selectedQuestion.id, 'textInput', value);
                                    handleQuestionChange(selectedQuestion.id, 'text', value);
                                  }}
                                  placeholder="Add Question Text here"
                                  variant="outlined"
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: '8px',
                                      borderColor: '#e5e7eb',
                                      backgroundColor: '#ffffff'
                                    },
                                    '& .MuiInputBase-input': {
                                      fontSize: '14px',
                                      color: '#374151'
                                    }
                                  }}
                                />
                              </Box>

                              {/* Add Limit and Add Format Buttons */}
                              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                <Button
                                  startIcon={<AddIcon sx={{ fontSize: 16, color: '#3b82f6' }} />}
                                  onClick={() => {
                                    if (selectedQuestion.format) {
                                      handleQuestionChange(selectedQuestion.id, 'format', '');
                                    }
                                    handleQuestionChange(selectedQuestion.id, 'limit', selectedQuestion.limit || '');
                                    handleQuestionChange(selectedQuestion.id, 'showLimitField', true);
                                  }}
                                  disabled={!!selectedQuestion.limit || !!selectedQuestion.showLimitField}
                                  sx={{
                                    color: '#3b82f6',
                                    textTransform: 'none',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    padding: '8px 12px',
                                    '&:hover': {
                                      backgroundColor: 'rgba(59, 130, 246, 0.04)'
                                    },
                                    '&:disabled': {
                                      color: '#9ca3af'
                                    }
                                  }}
                                >
                                  Add Limit
                                </Button>
                                
                                <Button
                                  startIcon={<AddIcon sx={{ fontSize: 16, color: '#3b82f6' }} />}
                                  onClick={() => {
                                    if (selectedQuestion.limit) {
                                      handleQuestionChange(selectedQuestion.id, 'limit', '');
                                      handleQuestionChange(selectedQuestion.id, 'showLimitField', false);
                                    }
                                    handleQuestionChange(selectedQuestion.id, 'format', selectedQuestion.format || '');
                                    handleQuestionChange(selectedQuestion.id, 'showFormatField', true);
                                  }}
                                  disabled={!!selectedQuestion.format || !!selectedQuestion.showFormatField}
                                  sx={{
                                    color: '#3b82f6',
                                    textTransform: 'none',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    padding: '8px 12px',
                                    '&:hover': {
                                      backgroundColor: 'rgba(59, 130, 246, 0.04)'
                                    },
                                    '&:disabled': {
                                      color: '#9ca3af'
                                    }
                                  }}
                                >
                                  Add Format
                                </Button>
                              </Box>

                              {/* Limit Field */}
                              {(selectedQuestion.limit !== undefined && selectedQuestion.limit !== '') || selectedQuestion.showLimitField ? (
                                <Box sx={{ mb: 2 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <Typography sx={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>
                                      Character Limit
                                    </Typography>
                                    <IconButton 
                                      size="small"
                                      onClick={() => {
                                        handleQuestionChange(selectedQuestion.id, 'limit', '');
                                        handleQuestionChange(selectedQuestion.id, 'showLimitField', false);
                                      }}
                                      sx={{ color: '#6b7280', padding: '4px' }}
                                    >
                                      <CloseIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Box>
                                  <TextField
                                    fullWidth
                                    value={selectedQuestion.limit || ''}
                                    onChange={(e) => {
                                      // Only allow numbers
                                      const value = e.target.value.replace(/[^0-9]/g, '');
                                      handleQuestionChange(selectedQuestion.id, 'limit', value);
                                    }}
                                    placeholder="Enter character limit"
                                    type="text"
                                    inputProps={{
                                      inputMode: 'numeric',
                                      pattern: '[0-9]*'
                                    }}
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                        borderColor: '#e5e7eb'
                                      },
                                      '& .MuiInputBase-input': {
                                        fontSize: '14px'
                                      }
                                    }}
                                  />
                                </Box>
                              ) : null}

                              {/* Format Field */}
                              {(selectedQuestion.format !== undefined && selectedQuestion.format !== '') || selectedQuestion.showFormatField ? (
                                <Box sx={{ mb: 2 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <Typography sx={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>
                                      Format Pattern
                                    </Typography>
                                    <IconButton 
                                      size="small"
                                      onClick={() => {
                                        handleQuestionChange(selectedQuestion.id, 'format', '');
                                        handleQuestionChange(selectedQuestion.id, 'showFormatField', false);
                                      }}
                                      sx={{ color: '#6b7280', padding: '4px' }}
                                    >
                                      <CloseIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Box>
                                  <TextField
                                    fullWidth
                                    value={selectedQuestion.format || ''}
                                    onChange={(e) => {
                                      // Only allow numbers and hyphens
                                      const value = e.target.value.replace(/[^0-9-]/g, '');
                                      handleQuestionChange(selectedQuestion.id, 'format', value);
                                    }}
                                    placeholder="Enter format pattern"
                                    type="text"
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                        borderColor: '#e5e7eb'
                                      },
                                      '& .MuiInputBase-input': {
                                        fontSize: '14px'
                                      }
                                    }}
                                  />
                                </Box>
                              ) : null}
                            </>
                          )}

                          {/* Options for option-based types */}
                          {isOptionBasedType(selectedQuestion.type) && (
                            <Box sx={{ marginBottom: '20px' }}>
                              {/* Text Input Field - Where user types the question */}
                              <Box sx={{ marginBottom: '20px' }}>
                                <TextField
                                  fullWidth
                                  multiline
                                  rows={4}
                                  value={selectedQuestion.textInput || ''}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    handleQuestionChange(selectedQuestion.id, 'textInput', value);
                                    handleQuestionChange(selectedQuestion.id, 'text', value);
                                  }}
                                  placeholder="Text"
                                  variant="outlined"
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: '8px',
                                      borderColor: '#e5e7eb',
                                      backgroundColor: '#ffffff'
                                    },
                                    '& .MuiInputBase-input': {
                                      fontSize: '14px',
                                      color: '#374151'
                                    }
                                  }}
                                />
                              </Box>

                              {selectedQuestion.options?.map((option, index) => (
                                <Box key={index} sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  marginBottom: '12px',
                                  border: '2px solid #3b82f6',
                                  borderRadius: '8px',
                                  padding: '12px'
                                }}>
                                  <Box sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(2, 4px)',
                                    gridTemplateRows: 'repeat(3, 4px)',
                                    gap: '2px',
                                    width: '12px',
                                    height: '14px',
                                    marginRight: '12px'
                                  }}>
                                    {[...Array(6)].map((_, i) => (
                                      <Box
                                        key={i}
                                        sx={{
                                          width: '3px',
                                          height: '3px',
                                          backgroundColor: '#3b82f6',
                                          borderRadius: '1px'
                                        }}
                                      />
                                    ))}
                                  </Box>
                                  <TextField
                                    fullWidth
                                    value={option}
                                    onChange={(e) => {
                                      const newOptions = [...(selectedQuestion.options || [])];
                                      newOptions[index] = e.target.value;
                                      handleQuestionChange(selectedQuestion.id, 'options', newOptions);
                                    }}
                                    placeholder={`Option 0${index + 1}`}
                                    variant="standard"
                                    sx={{
                                      '& .MuiInput-underline:before': {
                                        display: 'none'
                                      },
                                      '& .MuiInput-underline:after': {
                                        display: 'none'
                                      },
                                      '& .MuiInputBase-input': {
                                        fontSize: '14px',
                                        color: '#374151'
                                      }
                                    }}
                                  />
                                  <IconButton 
                                    onClick={() => {
                                      const newOptions = selectedQuestion.options.filter((_, i) => i !== index);
                                      handleQuestionChange(selectedQuestion.id, 'options', newOptions);
                                    }}
                                    sx={{ 
                                      color: '#3b82f6',
                                      width: '24px',
                                      height: '24px',
                                      marginLeft: '8px'
                                    }}
                                  >
                                    <CloseIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Box>
                              ))}
                              
                              {/* Add Option and Add Other Buttons */}
                              <Box sx={{ display: 'flex', gap: 2, marginTop: '16px' }}>
                                <Button
                                  startIcon={<AddIcon sx={{ fontSize: 16, color: '#3b82f6' }} />}
                                  onClick={() => {
                                    const newOptions = [...(selectedQuestion.options || []), ''];
                                    handleQuestionChange(selectedQuestion.id, 'options', newOptions);
                                  }}
                                  sx={{
                                    color: '#3b82f6',
                                    textTransform: 'none',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    padding: '8px 12px',
                                    '&:hover': {
                                      backgroundColor: 'rgba(59, 130, 246, 0.04)'
                                    }
                                  }}
                                >
                                  Add Option
                                </Button>
                                
                                <Button
                                  startIcon={<AddIcon sx={{ fontSize: 16, color: '#3b82f6' }} />}
                                  onClick={() => {
                                    const newOptions = [...(selectedQuestion.options || []), 'Other'];
                                    handleQuestionChange(selectedQuestion.id, 'options', newOptions);
                                  }}
                                  sx={{
                                    color: '#3b82f6',
                                    textTransform: 'none',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    padding: '8px 12px',
                                    '&:hover': {
                                      backgroundColor: 'rgba(59, 130, 246, 0.04)'
                                    }
                                  }}
                                >
                                  Add Other
                                </Button>
                              </Box>
                            </Box>
                          )}

                        </Box>

                        {/* Action Buttons Footer */}
                        <Box sx={{
                          padding: '16px 20px',
                          borderTop: '1px solid #f3f4f6',
                          backgroundColor: '#f9fafb',
                          borderRadius: '0 0 12px 12px',
                          flexShrink: 0 // Prevent footer from shrinking
                        }}>
                          <Box sx={{
                            display: 'flex',
                            gap: '12px'
                          }}>
                            <Button 
                              onClick={() => setSelectedQuestion(null)}
                              variant="outlined"
                              sx={{
                                flex: 1,
                                height: '48px',
                                fontWeight: 500,
                                textTransform: 'none',
                                borderRadius: '8px',
                                border: '1px solid #d1d5db',
                                color: '#3b82f6',
                                backgroundColor: '#ffffff',
                                '&:hover': {
                                  borderColor: '#3b82f6',
                                  backgroundColor: '#f8fafc'
                                }
                              }}
                            >
                              Cancel
                            </Button>
                            <Button 
                              variant="contained"
                              onClick={handleAddQuestion}
                              disabled={!isFormValid() || loading}
                              sx={{
                                flex: 1,
                                height: '48px',
                                fontWeight: 500,
                                textTransform: 'none',
                                borderRadius: '8px',
                                backgroundColor: isFormValid() ? '#3b82f6' : '#d1d5db',
                                color: isFormValid() ? '#ffffff' : '#9ca3af',
                                boxShadow: 'none',
                                '&:hover': isFormValid() ? {
                                  backgroundColor: '#2563eb',
                                  boxShadow: 'none'
                                } : {
                                  boxShadow: 'none'
                                },
                                '&:disabled': {
                                  backgroundColor: '#d1d5db',
                                  color: '#9ca3af',
                                  boxShadow: 'none'
                                }
                              }}
                            >
                              {selectedQuestion.isNew ? 'Add Question' : 'Update Question'}
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}

            {/* Add Section */}
            {isAddingSection && (
              <Paper sx={{
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                border: '2px dashed #d1d5db',
                marginBottom: '16px',
                padding: '0',
                '&:hover': {
                  borderColor: '#3b82f6'
                }
              }}>
                <TextField
                  fullWidth
                  autoFocus
                  placeholder="Enter section name"
                  value={newSectionName}
                  onChange={(e) => setNewSectionName(e.target.value)}
                  onKeyPress={handleSectionNameSubmit}
                  onBlur={() => {
                    if (!newSectionName.trim()) {
                      setIsAddingSection(false);
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        border: 'none',
                      },
                    },
                    '& .MuiInputBase-input': {
                      padding: '20px 24px',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#333333',
                    }
                  }}
                />
              </Paper>
            )}

            {!isAddingSection && (
              <Button
                startIcon={<AddCircleOutlineIcon />}
                onClick={handleAddSection}
                variant="outlined"
                sx={{
                  minWidth: '160px',
                  height: '48px',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '12px',
                  color: '#3b82f6',
                  borderColor: '#3b82f6',
                  padding: '12px 24px',
                  fontSize: '14px',
                  marginTop: '16px',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: '#eff6ff',
                    borderColor: '#2563eb',
                    color: '#2563eb',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.15)'
                  }
                }}
              >
                Add Section
              </Button>
            )}
          </Box>
        )}
      </Box>
      
      <AddTabModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onAddTab={() => {}}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmation.open}
        onClose={cancelDelete}
        aria-labelledby="delete-confirmation-dialog"
      >
        <DialogTitle id="delete-confirmation-dialog">Confirm Deletion</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this question?
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete}>Cancel</Button>
          <Button onClick={confirmDelete} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog for Restore/Delete Actions */}
      <Dialog
        open={confirmationDialog.open}
        onClose={() => setConfirmationDialog({ open: false, action: null, questionId: null })}
        aria-labelledby="confirmation-dialog"
      >
        <DialogTitle id="confirmation-dialog">
          {confirmationDialog.action === 'restore' ? 'Restore Question' : 'Delete Question'}
        </DialogTitle>
        <DialogContent>
          {confirmationDialog.action === 'restore' 
            ? 'Are you sure you want to restore this question?' 
            : 'Are you sure you want to delete this question?'
          }
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmationDialog({ open: false, action: null, questionId: null })}>
            Cancel
          </Button>
          <Button onClick={handleConfirmedAction} autoFocus>
            {confirmationDialog.action === 'restore' ? 'Restore' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Section Dropdown Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          sx: {
            boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.2)',
            borderRadius: '8px',
            mt: 1
          }
        }}
      >
        <MenuItem 
          disabled 
          sx={{
            fontWeight: 600,
            color: '#1f2937',
            backgroundColor: '#f9fafb'
          }}
        >
          Move to Section
        </MenuItem>
        {sections.map((sectionName) => {
          const activeQuestion = activeQuestionId && 
            Object.values(organizedQuestions)
              .flatMap(sectionData => Object.values(sectionData).flat())
              .find(q => (q._id || q.id) === activeQuestionId);
          
          const currentSection = activeQuestion ? 
            Object.keys(organizedQuestions).find(key => 
              Object.values(organizedQuestions[key] || {}).some(subsectionQuestions => 
                Array.isArray(subsectionQuestions) && subsectionQuestions.some(q => (q._id || q.id) === activeQuestionId)
              )
            ) : '';
          
          return (
            <MenuItem 
              key={sectionName}
              onClick={() => activeQuestion && handleMoveToSection(activeQuestion, currentSection, sectionName)}
              sx={{
                color: '#374151',
                backgroundColor: sectionName === currentSection ? '#3b82f6' : 'transparent',
                color: sectionName === currentSection ? '#ffffff' : '#374151',
                '&:hover': {
                  backgroundColor: sectionName === currentSection ? '#3b82f6' : '#eff6ff',
                  color: sectionName === currentSection ? '#ffffff' : '#3b82f6'
                }
              }}
            >
              {sectionName}
            </MenuItem>
          );
        })}
      </Menu>

      {/* Section Delete Confirmation Dialog */}
      <Dialog
        open={deleteSectionConfirmation.open}
        onClose={() => setDeleteSectionConfirmation({ open: false, sectionName: null })}
        aria-labelledby="delete-section-confirmation-dialog"
      >
        <DialogTitle id="delete-section-confirmation-dialog">Delete Section</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this section? All questions in this section will be deleted.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteSectionConfirmation({ open: false, sectionName: null })}>
            Cancel
          </Button>
          <Button onClick={confirmDeleteSection} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default Index; 